import React from 'react'
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { useIntl } from 'gatsby-plugin-intl'
import { calculatorData } from '../Calculator.data'
import { CustomSlider } from '../../CustomSlider/CustomSlider'
import { useStyles } from '../Calculator.style'
import { Minus, Plus } from '../../SVGIcons'
import { resolveImageUrlForDeviceAndLocale } from '../../../utils/resolveImageUrlForDeviceAndLocale'

const CalculatorForm = (data) => {
  const styles = useStyles()
  const { locale } = useIntl()

  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('sm'))

  const initialSliderState = calculatorData[data.type].sliders.reduce(
    (acc, slider) => ({ ...acc, [slider.sliderId]: slider.defaultValue }),
    {},
  )

  const [sliderValues, setSliderValues] = React.useState(initialSliderState)

  const handleChange = (sliderId) => (event, newValue) => {
    setSliderValues((prev) => ({ ...prev, [sliderId]: newValue }))
  }

  const handleIncrement = (sliderId, step, sliderMax) => () => {
    setSliderValues((prev) => {
      if (prev[sliderId] >= sliderMax) return prev
      return {
        ...prev,
        [sliderId]: prev[sliderId] + step,
      }
    })
  }

  const handleDecrement = (sliderId, step, sliderMin) => () => {
    setSliderValues((prev) => {
      if (prev[sliderId] <= sliderMin) return prev
      return {
        ...prev,
        [sliderId]: prev[sliderId] - step,
      }
    })
  }

  const { t } = data

  const handleCalculateClick = () => {
    // eslint-disable-next-line no-undef
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      // eslint-disable-next-line no-undef
      window.gtag('event', 'calculate_button_clicked', { ...data })
    }
    data.handleCalculate(data.type, sliderValues)
    data.handleState('report')
  }

  return (
    <Box>
      <Typography variant="h1" className={styles.calculatorTitle}>
        {t(`${data.type} Calculator title`)}
      </Typography>

      {calculatorData[data.type].description && (
        <Typography variant="body1" className={styles.calculatorSubtitle}>
          {t(`${data.type} Calculator description`)}
        </Typography>
      )}

      {calculatorData[data.type].image && (
        <Box className={styles.calculatorImage}>
          <img
            src={resolveImageUrlForDeviceAndLocale(
              calculatorData[data.type].image,
              locale,
              isDesktop,
              true,
            )}
          />
        </Box>
      )}

      <Box className={styles.calculatorSlidersContainer}>
        {calculatorData[data.type].sliders.map((slider, i) => {
          return (
            <Box key={i}>
              <Typography
                variant="body1"
                className={styles.calculatorSliderLabel}
              >
                {t(`${data.type} ${slider.sliderId} label`)}
              </Typography>
              <Typography variant="h4" className={styles.calculatorSliderTitle}>
                {`${sliderValues[slider.sliderId]?.toLocaleString()} `}
                {t(`${data.type} ${slider.sliderId} unit`)}
              </Typography>
              <Box className={styles.calculatorSliderContainer}>
                <IconButton
                  className={styles.calculatorActionIcon}
                  onClick={handleDecrement(
                    slider.sliderId,
                    slider.step || 1,
                    slider.min,
                  )}
                >
                  <Minus />
                </IconButton>
                <CustomSlider
                  aria-label="Volume"
                  className={styles.slider}
                  value={sliderValues[slider.sliderId]}
                  onChange={handleChange(slider.sliderId)}
                  min={slider.min}
                  max={slider.max}
                  step={slider.step || 1}
                />
                <IconButton
                  className={styles.calculatorActionIcon}
                  onClick={handleIncrement(
                    slider.sliderId,
                    slider.step || 1,
                    slider.max,
                  )}
                >
                  <Plus />
                </IconButton>
              </Box>
            </Box>
          )
        })}
      </Box>
      <Box className={styles.calculateButtonContainer}>
        <Button
          id="CALCULATE_BUTTON"
          variant="contained"
          color="secondary"
          className={styles.calculateButton}
          onClick={handleCalculateClick}
        >
          {t('Calculate')}
        </Button>
      </Box>
    </Box>
  )
}

export default CalculatorForm
